/**
 * 출납일보 **붙여넣기** 해석 — 시트·엑셀에서 복사한 칸(탭으로 나뉜 줄)을 출납 줄로 바꾼다(2026-09-28 way 「복붙 방식도 도입」).
 *
 * 칸 순서는 「회계팀 출납일보」 시트 그대로(`functions/src/cashbookImport.ts` 와 같은 모양):
 *   일자 | 구분(인출계좌/계정) | 시재계정 | 업체(기사) | 적요 | 금액 | 소계 | 증빙 | memo
 * - 🔴 시트는 날짜가 **블록 머리줄**이다(일자 칸에만 날짜) → 그 아래 줄들이 그 날짜(forward-fill).
 * - 일자 칸을 빼고 「구분」부터 복사해도 된다 — 금액이 5번째 칸이면 그렇게 읽고 날짜는 창에서 고른 날로.
 * - 머리줄(「일자 (요일)」)·금액이 숫자가 아닌 소제목 줄은 건너뛴다(건너뛴 수는 보여 준다).
 * 순수 함수 — 서버가 줄마다 다시 검증한다(`pasteCashBook`).
 */

export interface PastedRow {
  line: number; payDate: string; method: string; cashAccount: string; vendor: string; summary: string;
  amount: string; evidence: boolean | null; memo: string;
}

const norm = (v: string | undefined) => (v ?? '').replace(/\s+/g, ' ').trim();

/** 「2026년 1월 5일 월요일」·`2026-01-05`·`2026.1.5`·`1/5`(기본 연도) → YYYY-MM-DD. */
export function pasteDate(v: string, year: string): string | null {
  const s = norm(v);
  let m = s.match(/^(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일/) ?? s.match(/^(\d{4})[-./]\s*(\d{1,2})[-./]\s*(\d{1,2})\.?(\s*\(.\))?$/);
  if (m) return valid(m[1]!, m[2]!, m[3]!);
  m = s.match(/^(\d{1,2})\s*월\s*(\d{1,2})\s*일/) ?? s.match(/^(\d{1,2})[/.](\d{1,2})\.?(\s*\(.\))?$/);
  if (m) return valid(year, m[1]!, m[2]!);
  return null;
}
function valid(y: string, mo: string, d: string): string | null {
  const iso = `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  const t = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === iso ? iso : null;
}

/** `1,234,000`·`-5,000`·`(5,000)`·`₩1,000원` → `1234000`. 숫자가 아니면 null. */
export function pasteAmount(v: string): string | null {
  let s = norm(v).replace(/[,₩원\s]/g, '');
  if (/^\(.+\)$/.test(s)) s = `-${s.slice(1, -1)}`;
  return /^-?\d+(\.\d{1,2})?$/.test(s) ? s : null;
}

function pasteEvidence(v: string): boolean | null {
  const s = norm(v).toUpperCase();
  if (s === 'TRUE' || s === 'O' || s === 'Y' || s === '○' || s === '있음') return true;
  if (s === 'FALSE' || s === 'X' || s === 'N' || s === '없음') return false;
  return null;
}

export function parseCashPaste(text: string, defaultDate: string): { rows: PastedRow[]; skipped: number[]; withDate: boolean } {
  const lines = text.replace(/\r\n?/g, '\n').split('\n').map((l) => l.split('\t'));
  const year = defaultDate.slice(0, 4);
  // 칸 모양 — 머리줄이 있으면 그걸로, 없으면 금액이 6번째(일자 포함)인지 5번째(일자 뺌)인지 줄 수로 가른다.
  const head = lines.findIndex((c) => c.some((x) => norm(x).replace(/ /g, '') === '일자(요일)'));
  let withDate: boolean;
  let c0 = 0;
  if (head >= 0) {
    c0 = lines[head]!.findIndex((x) => norm(x).replace(/ /g, '') === '일자(요일)');
    withDate = true;
  } else {
    const at5 = lines.filter((c) => pasteAmount(c[5] ?? '') !== null).length;
    const at4 = lines.filter((c) => pasteAmount(c[4] ?? '') !== null).length;
    withDate = at5 >= at4;
  }
  const off = withDate ? c0 + 1 : 0; // 「구분」 칸 위치
  const rows: PastedRow[] = [];
  const skipped: number[] = [];
  let date = defaultDate;
  lines.forEach((c, i) => {
    if (i === head) return;
    if (c.every((x) => norm(x) === '')) return; // 빈 줄은 조용히
    if (withDate) {
      const d = pasteDate(c[c0] ?? '', year);
      if (d) {
        date = d;
        // 날짜만 있는 머리줄이면 다음 줄로. 같은 줄에 내용이 있으면(일자 칸을 매 줄 채운 경우) 그대로 읽는다.
        if ([0, 1, 2, 3, 4].every((k) => norm(c[off + k]) === '')) return;
      }
    }
    const amount = pasteAmount(c[off + 4] ?? '');
    const text4 = [0, 1, 2, 3].map((k) => norm(c[off + k]));
    if (amount === null || !text4.some(Boolean)) { skipped.push(i + 1); return; }
    rows.push({
      line: i + 1, payDate: date, method: text4[0]!, cashAccount: text4[1]!, vendor: text4[2]!, summary: text4[3]!, amount,
      // 금액 뒤 칸: 소계(버림) · 증빙 · memo
      evidence: pasteEvidence(c[off + 6] ?? ''), memo: norm(c[off + 7]),
    });
  });
  return { rows, skipped, withDate };
}
